import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';

import LoginView from '@/views/LoginView.vue';
import LoginGithubCallbackView from '@/views/LoginGithubCallbackView.vue';
import DashboardView from '@/views/DashboardView.vue';
import SimsView from '@/views/SimsView.vue';
import ApnsView from '@/views/ApnsView.vue';
import CustomersView from '@/views/CustomersView.vue';
import DevicesView from '@/views/DevicesView.vue';
import MessagesView from '@/views/MessagesView.vue';
import CoverageView from '@/views/CoverageView.vue';
import DemoView from '@/views/IotDemoView.vue';
import UserView from '@/views/UsersView.vue';
import CodecsView from '@/views/CodecsView.vue';

// Router configuration
const routes = [
  { path: '/', name: 'Dashboard', component: DashboardView, meta: { requiresAuth: true } },
  { path: '/login', name: 'Login', component: LoginView },
  { path: '/github-callback', name: 'GitHubCallback', component: LoginGithubCallbackView },
  { path: '/sims', name: 'SIMs', component: SimsView, meta: { requiresAuth: true } },
  { path: '/apns', name: 'APNs', component: ApnsView, meta: { requiresAuth: true } },
  { path: '/customers', name: 'Customers', component: CustomersView, meta: { requiresAuth: true } },
  { path: '/devices', name: 'Devices', component: DevicesView, meta: { requiresAuth: true } },
  { path: '/messages', name: 'Messages', component: MessagesView, meta: { requiresAuth: true } },
  { path: '/coverage', name: 'Coverage', component: CoverageView, meta: { requiresAuth: true } },
  { path: '/codecs', name: 'Codecs', component: CodecsView, meta: { requiresAuth: true }  },
  { path: '/demo', name: 'Demo', component: DemoView, meta: { requiresAuth: true } },
  { path: '/users', name: 'Users', component: UserView, meta: { requiresAuth: true, requiresAdmin: true } },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/AccessDenied.vue'),
    props: route => ({ 
      type: route.query.type, // '401' or '403'
      from: route.query.from 
    }),
  },
  {
    path: '/service-interruption',
    name: 'ServiceInterruption',
    component: () => import('@/views/ServiceInterruption.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  linkActiveClass: 'active',
});

// Navigation guard
const authPages = ['Login', 'GithubCallback', 'AccessDenied'];
const landingPages = ['ServiceInterruption', 'AccessDenied' ];
router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const schema = useSchemaStore();
  
  if (landingPages.includes(to.name)) return;

  // 1. Initial Session Hydration (IAP / Dev User / Persistent JWT)
  // Only run if we don't have a user, but we're NOT on the login page
  if (!auth.isHydrated) {
    try {
      await auth.checkSession();
    } catch (err) {
      const isNetworkError = 
        !navigator.onLine || 
        // err.response?.status >= 500 || // 502/503/504 server failures
        err.message?.includes('Network Error') || 
        err.message?.includes('Service Interruption') || 
        err.code === 'ECONNABORTED';
      if (isNetworkError) {
        return { name: 'ServiceInterruption' };
      }
    }
  }
  // 2. Auth Gate
  if (!auth.isAuthenticated && to.name !== 'Login') {
    return { name: 'Login' }; // Redirect if checkSession failed
  }
  // 3. Schema Gate (Only if authenticated)
  if (auth.isAuthenticated && to.name !== 'Login') {
    try {
      // fetchSchema handles its own "if already cached" logic
      await schema.fetchSchema();
    } catch (err) {
      console.error("Failed to load DB schema", err);
      // Optional: return '/error' or a page explaining the backend is down
      return false;
    }
  }
  // 4. Role-based Protection (Optional)
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'AccessDenied' };
  }
});

export default router;
